import { notifications } from '@mantine/notifications';
import { IconCheck, IconExclamationCircle } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Endpoints } from '@xver/apis';
import type { AddSSHKeyRequestModel } from '@xver/apis/v1/ssh-keys/model';
import { getErrorMessage } from '@xver/utils/error';
import { useTranslation } from 'react-i18next';

import { SSH_KEYS_QUERY_KEY } from './useSSHKeysQuery';

export function useSSHKeyMutations() {
    const { t } = useTranslation('sshKeys');
    const queryClient = useQueryClient();

    const invalidate = () => queryClient.invalidateQueries({ queryKey: SSH_KEYS_QUERY_KEY });

    const showError = (err: unknown) => {
        notifications.show({
            title: t('toast.errorTitle'),
            message: getErrorMessage(err) ?? t('toast.errorMessage'),
            icon: <IconExclamationCircle size={18} color="#eb5757" />,
        });
    };

    const create = useMutation({
        mutationFn: (payload: AddSSHKeyRequestModel) => Endpoints.v1.SSHKeys.addSSHKey(payload),
        onSuccess: async () => {
            notifications.show({
                title: t('toast.createTitle'),
                message: t('toast.createMessage'),
                icon: <IconCheck size={18} color="#22c55e" />,
            });
            await invalidate();
        },
        onError: showError,
    });

    const remove = useMutation({
        mutationFn: (id: number) => Endpoints.v1.SSHKeys.deleteSSHKey(id),
        onSuccess: async () => {
            notifications.show({
                title: t('toast.deleteTitle'),
                message: t('toast.deleteMessage'),
                icon: <IconCheck size={18} color="#22c55e" />,
            });
            await invalidate();
        },
        onError: showError,
    });

    return { create, remove };
}
